import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mic, Mail, CheckCircle, Sparkles, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import clsx from 'clsx';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const requestInFlight = useRef(false);

  const { sendMagicLink, loading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/dashboard';

  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  const validateEmail = () => {
    const newErrors = {};
    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Email is invalid';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();

    // Prevent duplicate submissions
    if (requestInFlight.current || isSubmitting) {
      return;
    }

    if (!validateEmail()) {
      return;
    }

    requestInFlight.current = true;
    setIsSubmitting(true);
    setErrors({});

    try {
      const result = await sendMagicLink(email);

      if (result.success) {
        setIsEmailSent(true);
      } else {
        setErrors({ general: result.error || 'Failed to send magic link' });
      }
    } catch (error) {
      setErrors({ general: 'An unexpected error occurred' });
    } finally {
      setIsSubmitting(false);
      requestInFlight.current = false;
    }
  };

  const handleResendLink = async () => {
    // Prevent duplicate submissions
    if (requestInFlight.current || isSubmitting) {
      return;
    }

    requestInFlight.current = true;
    setIsSubmitting(true);
    setErrors({});

    try {
      const result = await sendMagicLink(email);
      if (result.success) {
        setIsEmailSent(true);
      } else {
        setErrors({ general: result.error || 'Failed to resend magic link' });
      }
    } catch (error) {
      setErrors({ general: 'An unexpected error occurred' });
    } finally {
      setIsSubmitting(false);
      requestInFlight.current = false;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <LoadingSpinner size="lg" text="Loading..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-md w-full mx-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-2xl mb-4">
            <Mic className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Alfred Voice Platform</h1>
          <p className="text-gray-600">
            {isEmailSent ? 'Check your email for the magic link' : 'Enter your email to sign in'}
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {!isEmailSent ? (
            <>
              {errors.general && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
                  <AlertCircle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0" />
                  <span className="text-red-700 text-sm">{errors.general}</span>
                </div>
              )}

              <form onSubmit={handleEmailSubmit} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={clsx(
                        'block w-full pl-10 pr-3 py-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                        errors.email
                          ? 'border-red-300 bg-red-50'
                          : 'border-gray-300 bg-white'
                      )}
                      placeholder="your.email@example.com"
                      autoFocus
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={clsx(
                    'w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white transition-colors duration-200',
                    isSubmitting
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                  )}
                >
                  {isSubmitting ? (
                    <LoadingSpinner size="sm" color="white" className="mr-2" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  {isSubmitting ? 'Sending...' : 'Send Magic Link'}
                </button>

                <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-xs text-blue-800 text-center">
                    <strong>No password needed!</strong> We&apos;ll email you a secure link to sign in.
                  </p>
                </div>
              </form>
            </>
          ) : (
            <div className="space-y-6">
              {/* Success message */}
              <div className="p-6 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-green-800 font-semibold mb-1">Magic link sent!</p>
                    <p className="text-sm text-green-700 mb-2">Check your email at:</p>
                    <p className="text-sm font-medium text-green-900">{email}</p>
                  </div>
                </div>
              </div>

              {/* Instructions */}
              <div className="bg-gray-50 rounded-lg p-6 space-y-3">
                <h3 className="font-semibold text-gray-900 text-sm">Next steps:</h3>
                <ol className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold mr-2 flex-shrink-0 mt-0.5">1</span>
                    <span>Open the email we just sent you</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold mr-2 flex-shrink-0 mt-0.5">2</span>
                    <span>Click the &quot;Sign In&quot; button in the email</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold mr-2 flex-shrink-0 mt-0.5">3</span>
                    <span>You&apos;ll be automatically logged in!</span>
                  </li>
                </ol>
              </div>

              {/* Warning */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-xs text-yellow-800">
                  ⏱️ The link expires in <strong>10 minutes</strong> and can only be used once.
                </p>
              </div>

              {/* Resend option */}
              <div className="text-center pt-4">
                <p className="text-sm text-gray-600 mb-3">Didn&apos;t receive the email?</p>
                <button
                  onClick={handleResendLink}
                  disabled={isSubmitting}
                  className="text-blue-600 hover:text-blue-500 font-medium text-sm transition-colors duration-200 disabled:opacity-50"
                >
                  {isSubmitting ? 'Sending...' : 'Resend magic link'}
                </button>
              </div>

              {/* Back to email */}
              <div className="text-center pt-2 border-t">
                <button
                  onClick={() => {
                    setIsEmailSent(false);
                    setErrors({});
                    requestInFlight.current = false;
                  }}
                  className="text-gray-600 hover:text-gray-800 text-sm transition-colors duration-200"
                >
                  ← Use a different email
                </button>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Don&apos;t have an account?{' '}
              <Link
                to="/register"
                className="font-medium text-blue-600 hover:text-blue-500 transition-colors duration-200"
              >
                Sign up here
              </Link>
            </p>
          </div>

          {/* Help Text */}
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-600 text-center">
              Need help? Contact support at{' '}
              <a href="mailto:hello@relaywork.co" className="text-blue-600 hover:text-blue-500">
                hello@relaywork.co
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;